import React from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Calendar, DollarSign, Briefcase, Camera, Video, CheckCircle, XCircle, Clock, Users, FileText, CreditCard, MessageSquare, Star, RotateCcw, Mail, ScrollText, PenTool, AlertTriangle, Pencil, X, Save, Loader2, UserCheck, EyeOff, Undo2 } from "lucide-react";
import { JOB_REQUIRED_SKILLS, GENRES, HIREABLE_ROLES } from "@/lib/profileOptions";

function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();

  const [job, setJob] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [applying, setApplying] = React.useState(false);
  const [applicationMessage, setApplicationMessage] = React.useState("");
  const [showReviewForm, setShowReviewForm] = React.useState(searchParams.get("review") === "true");
  const [reviewRating, setReviewRating] = React.useState(0);
  const [reviewComment, setReviewComment] = React.useState("");
  const [submittingReview, setSubmittingReview] = React.useState(false);
  const [existingReview, setExistingReview] = React.useState(null);
  const [proposedRate, setProposedRate] = React.useState("");
  const [showApplyDialog, setShowApplyDialog] = React.useState(false);
  const [hireInfo, setHireInfo] = React.useState(null);
  const [contractInfo, setContractInfo] = React.useState(null);
  const [editSection, setEditSection] = React.useState(null); // "details", "datetime", "pay"
  const [editData, setEditData] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  // Hire dialog state (for applicants)
  const [hireDialogOpen, setHireDialogOpen] = React.useState(false);
  const [hireCandidate, setHireCandidate] = React.useState(null);
  const [hireMessage, setHireMessage] = React.useState("");
  const [finalRate, setFinalRate] = React.useState("");
  const [rateType, setRateType] = React.useState("hourly");
  const [hiring, setHiring] = React.useState(false);
  const [contractTemplates, setContractTemplates] = React.useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState("");
  const [existingContract, setExistingContract] = React.useState(null);

  React.useEffect(() => { fetchJob(); fetchHireInfo(); fetchContract(); }, [jobId]);

  function fetchJob() {
    const headers = token ? { Authorization: "Bearer " + token } : {};
    // Add cache-busting timestamp to prevent stale data
    fetch(`${API}/jobs/${jobId}?_t=${Date.now()}`, { headers })
      .then((r) => { if (r.ok) return r.json(); throw new Error("Not found"); })
      .then((data) => { setJob(data); setLoading(false); })
      .catch(() => { toast.error("Failed to load job"); setLoading(false); navigate("/jobs"); });
  }

  function fetchHireInfo() {
    if (!token) return;
    fetch(API + "/jobs/" + jobId + "/hire-info", { headers: { Authorization: "Bearer " + token } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setHireInfo(data); })
      .catch(() => {});
  }

  function fetchContract() {
    if (!token) return;
    fetch(API + "/contracts/for-job/" + jobId, { headers: { Authorization: "Bearer " + token } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.contract) setContractInfo(data.contract); })
      .catch(() => {});
  }

  function handleReopenJob() {
    if (!window.confirm("Are you sure you want to reopen this job?")) return;
    fetch(API + "/jobs/" + jobId + "/reopen", { method: "POST", headers: { Authorization: "Bearer " + token } })
      .then((r) => {
        if (r.ok) {
          toast.success("Job reopened");
          fetchJob();
          setHireInfo(null);
        } else {
          toast.error("Failed to reopen job");
        }
      });
  }

  function handleMarkFilled() {
    if (!window.confirm("Mark this job as filled? (e.g., if you hired someone outside the platform)")) return;
    fetch(API + "/jobs/" + jobId + "/mark-filled", { method: "POST", headers: { Authorization: "Bearer " + token } })
      .then((r) => {
        if (r.ok) {
          toast.success("Job marked as filled");
          fetchJob();
        } else {
          toast.error("Failed to mark as filled");
        }
      });
  }

  function handleMarkHired() {
    if (!window.confirm("Mark this job as hired? This confirms the candidate was hired even if they didn't formally accept through the platform.")) return;
    fetch(API + "/jobs/" + jobId + "/mark-hired", { method: "POST", headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" }, body: JSON.stringify({}) })
      .then((r) => {
        if (r.ok) {
          toast.success("Job marked as hired");
          fetchJob();
          fetchHireInfo();
        } else {
          r.json().then((e) => toast.error(e.detail || "Failed"));
        }
      });
  }

  function handleMarkCompleted() {
    if (!window.confirm("Mark this job as completed?")) return;
    fetch(API + "/jobs/" + jobId + "/mark-completed", { method: "POST", headers: { Authorization: "Bearer " + token } })
      .then((r) => {
        if (r.ok) {
          toast.success("Job marked as completed");
          fetchJob();
        } else {
          r.json().then((e) => toast.error(e.detail || "Failed"));
        }
      });
  }

  function handleWithdrawJob() {
    if (!window.confirm("Withdraw this job? It will be archived and hidden from the main dashboard.")) return;
    fetch(API + "/jobs/" + jobId + "/withdraw-job", { method: "POST", headers: { Authorization: "Bearer " + token } })
      .then((r) => {
        if (r.ok) {
          toast.success("Job withdrawn and archived");
          fetchJob();
        } else {
          r.json().then((e) => toast.error(e.detail || "Failed"));
        }
      });
  }

  // Check if user has already reviewed
  React.useEffect(() => {
    if (job && user && token) {
      fetch(API + "/jobs/reviews/user/" + user.user_id, { headers: { Authorization: "Bearer " + token } })
        .then(r => r.ok ? r.json() : { reviews: [] })
        .then(data => {
          var existing = (data.reviews || []).find(r => r.job_id === jobId);
          if (existing) setExistingReview(existing);
        })
        .catch(() => {});
    }
  }, [job, user, token, jobId]);

  function submitReview() {
    if (reviewRating < 1) return toast.error("Please select a rating");
    if (!job) return;
    var reviewedUserId = isOwner
      ? (job.hired_candidate_id || (hireInfo && hireInfo.candidate_id))
      : (job.created_by || job.studio_id);
    if (!reviewedUserId) return toast.error("Cannot determine who to review");

    setSubmittingReview(true);
    fetch(API + "/jobs/reviews", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ rating: reviewRating, comment: reviewComment, job_id: jobId, reviewed_user_id: reviewedUserId })
    })
      .then(r => {
        if (r.ok) {
          toast.success("Review submitted!");
          setShowReviewForm(false);
          setExistingReview({ rating: reviewRating, comment: reviewComment });
        } else {
          r.json().then(e => toast.error(e.detail || "Failed to submit review"));
        }
      })
      .catch(() => toast.error("Failed to submit review"))
      .finally(() => setSubmittingReview(false));
  }



  function handleCancelJob() {
    if (!window.confirm("Are you sure you want to cancel this job listing? This cannot be undone.")) return;
    fetch(API + "/jobs/" + jobId + "/cancel", { method: "POST", headers: { Authorization: "Bearer " + token } })
      .then((r) => {
        if (r.ok) {
          toast.success("Job cancelled");
          fetchJob();
        } else {
          r.json().then((e) => toast.error(e.detail || "Failed to cancel"));
        }
      });
  }

  function handleApply() {
    setApplying(true);
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = "Bearer " + token;

    const body = { message: applicationMessage };
    if (proposedRate) body.proposed_rate = parseFloat(proposedRate);

    fetch(API + "/jobs/" + jobId + "/apply", { method: "POST", headers, body: JSON.stringify(body) })
      .then((r) => {
        if (r.ok) { toast.success("Application submitted!"); setShowApplyDialog(false); fetchJob(); return; }
        return r.json().then((e) => { toast.error(e.detail || "Failed to apply"); });
      })
      .catch(() => toast.error("Failed to submit application"))
      .finally(() => setApplying(false));
  }

  function handleApplicationStatus(appId, status) {
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = "Bearer " + token;
    fetch(API + "/applications/" + appId + "/status?status=" + status, { method: "PUT", headers })
      .then((r) => { if (r.ok) { toast.success("Application " + status); fetchJob(); } else { toast.error("Failed to update"); } })
      .catch(() => toast.error("Failed to update"));
  }

  async function handleHireApplicant(app) {
    const candidateId = app.user_id || app.applicant_id;
    const candidateName = app.user_name || app.applicant?.name || "Unknown";
    setHireCandidate({ user_id: candidateId, name: candidateName, application_id: app.application_id });
    setHireMessage("");
    setFinalRate(app.proposed_rate ? String(app.proposed_rate) : "");
    setRateType(job?.payment_type || (job?.hourly_rate ? "hourly" : job?.flat_rate ? "flat" : "hourly"));
    setSelectedTemplate(job?.contract_template_id || "");
    setExistingContract(null);
    setHireDialogOpen(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tmplRes, checkRes] = await Promise.all([
        fetch(`${API}/contracts/templates`, { headers }),
        fetch(`${API}/contracts/check/${candidateId}`, { headers }),
      ]);
      if (tmplRes.ok) {
        const data = await tmplRes.json();
        setContractTemplates(data.templates);
        if (job?.contract_template_id && data.templates.some(t => t.template_id === job.contract_template_id)) {
          setSelectedTemplate(job.contract_template_id);
        }
      }
      if (checkRes.ok) {
        const data = await checkRes.json();
        if (data.has_contract) setExistingContract(data.contract);
      }
    } catch {}
  }

  async function confirmHireApplicant() {
    if (!hireCandidate) return;
    setHiring(true);
    try {
      // Step 1: Create contract if template selected
      if (selectedTemplate) {
        const contractParams = new URLSearchParams({
          job_id: jobId,
          candidate_id: hireCandidate.user_id,
          template_id: selectedTemplate,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        if (finalRate.trim()) contractParams.set("final_rate", finalRate.trim());
        const cRes = await fetch(`${API}/contracts/create-for-hire?${contractParams.toString()}`, {
          method: "POST", headers: { Authorization: `Bearer ${token}` }
        });
        if (!cRes.ok) {
          const err = await cRes.json();
          toast.error(err.detail || "Failed to create contract");
          setHiring(false);
          return;
        }
      }
      // Step 2: Hire the candidate
      const res = await fetch(`${API}/jobs/${jobId}/hire/${hireCandidate.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: hireMessage.trim() || null, final_rate: finalRate.trim() || null, rate_type: rateType })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Successfully hired ${hireCandidate.name}!`);
        setHireDialogOpen(false);
        fetchJob();
        fetchHireInfo();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to hire candidate");
      }
    } catch {
      toast.error("Network error - please try again");
    } finally {
      setHiring(false);
    }
  }

  function handleDismissApplicant(appId) {
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API}/jobs/${jobId}/applications/${appId}/dismiss`, { method: "POST", headers })
      .then((r) => { if (r.ok) { fetchJob(); } else { toast.error("Failed to dismiss"); } })
      .catch(() => toast.error("Failed to dismiss"));
  }

  function handleRestoreApplicant(appId) {
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API}/jobs/${jobId}/applications/${appId}/restore`, { method: "POST", headers })
      .then((r) => { if (r.ok) { fetchJob(); } else { toast.error("Failed to restore"); } })
      .catch(() => toast.error("Failed to restore"));
  }

  function startEdit(section) {
    const data = {};
    if (section === "details") {
      data.title = job.title || "";
      data.description = job.description || "";
      data.role_needed = job.role_needed || "";
      data.requires_lead = job.requires_lead || false;
      data.genre = job.genre || "";
      data.required_skills = job.required_skills || [];
    } else if (section === "datetime") {
      // Extract date portion directly without timezone conversion
      // job.date might be "2026-02-19" or "2026-02-19T10:00:00"
      data.date = job.date ? job.date.split("T")[0] : "";
      data.start_time = job.start_time || "";
      data.end_time = job.end_time || "";
      data.venue_name = job.venue_name || "";
      data.timezone = job.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const ld = job.location_info || job.location_display || {};
      data.city = ld.city || "";
      data.state = ld.state || "";
      data.zip_code = ld.zip_code || "";
    } else if (section === "pay") {
      data.payment_type = job.payment_type || (job.hourly_rate ? "hourly" : job.flat_rate ? "flat" : "flexible");
      data.hourly_rate = job.hourly_rate || job.pay_rate || "";
      data.flat_rate = job.flat_rate || "";
      data.rate_min = job.rate_min || "";
      data.rate_max = job.rate_max || "";
      data.is_flexible = job.is_flexible || false;
    }
    setEditData(data);
    setEditSection(section);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/jobs/${jobId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editData),
      });
      if (res.ok) {
        toast.success("Job updated");
        setEditSection(null);
        fetchJob();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  }

  function toggleEditSkill(value) {
    const skills = editData.required_skills || [];
    setEditData({
      ...editData,
      required_skills: skills.includes(value) ? skills.filter(s => s !== value) : [...skills, value]
    });
  }

  function formatDate(dateStr) {
    if (!dateStr) return "TBD";
    // Handle date string - if it's just YYYY-MM-DD, parse it as local date
    // to avoid timezone conversion issues
    const datePart = dateStr.split("T")[0];
    const [year, month, day] = datePart.split("-").map(Number);
    const d = new Date(year, month - 1, day); // month is 0-indexed
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  }

  function formatTimeRange() {
    if (!job) return "";
    const st = job.start_time;
    const et = job.end_time;
    if (!st && !et) return "";
    const fmt = (t) => {
      if (!t) return "";
      const [h, m] = t.split(":");
      const hr = parseInt(h);
      const ampm = hr >= 12 ? "PM" : "AM";
      const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
      return `${h12}:${m} ${ampm}`;
    };
    if (st && et) return `${fmt(st)} – ${fmt(et)}`;
    if (st) return `Starting ${fmt(st)}`;
    return "";
  }

  if (loading) {
    return (<div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="text-white/40 text-lg">Loading...</div></div>);
  }
  if (!job) return null;

  const creatorId = job.created_by || job.studio_id;
  const isOwner = user && user.user_id === creatorId;
  const apps = job.applications || [];
  const hasApplied = user && apps.some((a) => a.user_id === user.user_id || a.applicant_id === user.user_id);
  const canApply = user && (user.roles || []).includes("assistant") && job.status === "open" && !hasApplied && !isOwner;

  // Location
  const ld = job.location_display || job.location_info || {};
  const locationText = ld.city ? `${ld.city}${ld.state ? ", " + ld.state : ""}` : (typeof job.location === "object" && job.location?.city ? job.location.city : "Location TBD");

  // Compensation
  const rate = job.hourly_rate || job.pay_rate;
  const compensationText = rate ? `$${rate}/hr` : (job.flat_rate ? `$${job.flat_rate} flat` : (job.is_flexible ? `$${job.rate_min || "?"}–$${job.rate_max || "?"}/hr (flexible)` : null));

  // Venue
  const venueText = job.venue_name || "";

  // Time
  const timeRange = formatTimeRange();

  // Job type icon
  const jobTypeIcon = job.job_type === "photo" ? <Camera className="w-5 h-5" /> : job.job_type === "video" ? <Video className="w-5 h-5" /> : <Briefcase className="w-5 h-5" />;

  const statusClass = job.status === "open" ? "bg-white/10 text-white border-white/20" : job.status === "filled" ? "bg-green-500/10 text-green-400 border-green-500/20" : job.status === "cancelled" ? "bg-red-500/10 text-red-400 border-red-500/20" : job.status === "pending_confirmation" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : job.status === "expired" ? "bg-amber-500/10 text-amber-400/70 border-amber-500/20" : job.status === "completed" ? "bg-white/5 text-white/50 border-white/10" : job.status === "withdrawn" ? "bg-white/5 text-white/30 border-white/10" : "bg-white/10 text-white/60 border-white/20";
  const statusLabel = job.status === "pending_confirmation" ? "Pending Hire" : job.status;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        {user?.roles?.includes("assistant") && user?.assistant_profile?.find_work_active !== false && (
          <Link to="/jobs" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-browse-jobs">
            Browse Jobs
          </Link>
        )}
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8" data-testid="back-to-jobs">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Back to Dashboard</span>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              <Card className="bg-white/[0.03] border-white/10 p-8 mb-6" data-testid="job-detail-card">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Badge className={statusClass + " rounded-sm"}>{statusLabel}</Badge>
                    {isOwner && !editSection && (
                      <Button size="sm" variant="outline" onClick={() => startEdit("details")} className="border-white/20 text-white hover:bg-white/10" data-testid="edit-job-btn">
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit Job
                      </Button>
                    )}
                  </div>
                  {canApply && (
                    <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold uppercase tracking-wide" data-testid="apply-btn">Apply Now</Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white">
                        <DialogHeader><DialogTitle className="text-2xl">Apply for this Job</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="text-sm text-white/60 block mb-2">Your Message</label>
                            <Textarea value={applicationMessage} onChange={(e) => setApplicationMessage(e.target.value)} placeholder="Introduce yourself..." className="bg-white/[0.05] border-white/10 text-white min-h-[120px] rounded-sm" data-testid="application-message" />
                          </div>
                          <div>
                            <label className="text-sm text-white/60 block mb-2">Proposed Hourly Rate (optional)</label>
                            <Input type="number" value={proposedRate} onChange={(e) => setProposedRate(e.target.value)} placeholder="$50" className="bg-white/[0.05] border-white/10 text-white rounded-sm" data-testid="proposed-rate" />
                          </div>
                          <Button onClick={handleApply} disabled={applying} className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold" data-testid="submit-application-btn">{applying ? "Submitting..." : "Submit Application"}</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  {hasApplied && !isOwner && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 rounded-sm">Applied</Badge>}
                </div>

                {/* Job Details Section - editable */}
                {editSection === "details" ? (
                  <div className="space-y-4 mb-6 p-4 border border-white/20 rounded-sm" data-testid="edit-details-form">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold">Edit Job Details</h2>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditSection(null)} className="text-white/60"><X className="w-4 h-4" /></Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-white text-black hover:bg-gray-200" data-testid="save-details-btn">
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-white/60 block mb-1">Title</label>
                      <Input value={editData.title} onChange={(e) => setEditData({...editData, title: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-white/60 block mb-1">Role</label>
                        <select value={editData.role_needed} onChange={(e) => setEditData({...editData, role_needed: e.target.value})} className="w-full h-10 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3" data-testid="edit-role">
                          {HIREABLE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                          <input type="checkbox" checked={editData.requires_lead || false} onChange={(e) => setEditData({...editData, requires_lead: e.target.checked})} className="rounded border-white/20 bg-white/5" />
                          <span className="text-sm text-white/60">Requires lead experience</span>
                        </label>
                      </div>
                      <div>
                        <label className="text-sm text-white/60 block mb-1">Genre</label>
                        <select value={editData.genre} onChange={(e) => setEditData({...editData, genre: e.target.value})} className="w-full h-10 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3" data-testid="edit-genre">
                          {GENRES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-white/60 block mb-1">Description</label>
                      <Textarea value={editData.description} onChange={(e) => setEditData({...editData, description: e.target.value})} className="bg-white/[0.05] border-white/10 text-white min-h-[100px]" data-testid="edit-description" />
                    </div>
                    <div>
                      <label className="text-sm text-white/60 block mb-2">Required Skills</label>
                      <div className="flex flex-wrap gap-2">
                        {JOB_REQUIRED_SKILLS.map((skill) => {
                          const sel = (editData.required_skills || []).includes(skill.value);
                          return (
                            <button key={skill.value} type="button" onClick={() => toggleEditSkill(skill.value)} className={`px-3 py-1 text-sm rounded-full border transition-all ${sel ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:border-white/30"}`}>
                              {skill.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative group">
                    {isOwner && (
                      <button onClick={() => startEdit("details")} className="absolute top-0 right-0 p-2 text-white/40 hover:text-white transition-opacity" data-testid="edit-details-btn">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" data-testid="job-title">{job.title}</h1>
                    {job.description && (
                      <div className="border-t border-white/10 pt-6">
                        <h2 className="text-xl font-semibold mb-4">Description</h2>
                        <p className="text-white/70 leading-relaxed whitespace-pre-wrap">{job.description}</p>
                      </div>
                    )}
                    {job.required_skills && job.required_skills.length > 0 && (
                      <div className="border-t border-white/10 pt-6 mt-6">
                        <h2 className="text-xl font-semibold mb-4">Required Skills</h2>
                        <div className="flex flex-wrap gap-2">
                          {job.required_skills.map((s) => <Badge key={s} className="bg-white/10 text-white border-white/20 rounded-sm">{JOB_REQUIRED_SKILLS.find(sk => sk.value === s)?.label || s}</Badge>)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Date & Location Section - editable */}
                {editSection === "datetime" ? (
                  <div className="space-y-4 mt-6 p-4 border border-white/20 rounded-sm" data-testid="edit-datetime-form">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold">Edit Date & Location</h2>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditSection(null)} className="text-white/60"><X className="w-4 h-4" /></Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-white text-black hover:bg-gray-200" data-testid="save-datetime-btn">
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-white/60 block mb-1">Date</label>
                        <Input type="date" value={editData.date} onChange={(e) => setEditData({...editData, date: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-date" />
                      </div>
                      <div>
                        <label className="text-sm text-white/60 block mb-1">Start</label>
                        <Input type="time" value={editData.start_time} onChange={(e) => setEditData({...editData, start_time: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-start" />
                      </div>
                      <div>
                        <label className="text-sm text-white/60 block mb-1">End</label>
                        <Input type="time" value={editData.end_time} onChange={(e) => setEditData({...editData, end_time: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-end" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-white/60 block mb-1">Timezone</label>
                      <select 
                        value={editData.timezone} 
                        onChange={(e) => setEditData({...editData, timezone: e.target.value})} 
                        className="w-full h-10 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3"
                        data-testid="edit-timezone"
                      >
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="America/Phoenix">Arizona (no DST)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                        <option value="America/Anchorage">Alaska Time (AKT)</option>
                        <option value="Europe/London">London (GMT/BST)</option>
                        <option value="Europe/Paris">Central European (CET)</option>
                        <option value="Asia/Tokyo">Japan (JST)</option>
                        <option value="Australia/Sydney">Sydney (AEST)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-white/60 block mb-1">Venue Name</label>
                      <Input value={editData.venue_name} onChange={(e) => setEditData({...editData, venue_name: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-venue" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-white/60 block mb-1">City</label>
                        <Input value={editData.city} onChange={(e) => setEditData({...editData, city: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-city" />
                      </div>
                      <div>
                        <label className="text-sm text-white/60 block mb-1">State</label>
                        <Input value={editData.state} onChange={(e) => setEditData({...editData, state: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-state" />
                      </div>
                      <div>
                        <label className="text-sm text-white/60 block mb-1">ZIP</label>
                        <Input value={editData.zip_code} onChange={(e) => setEditData({...editData, zip_code: e.target.value})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-zip" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative group mt-6 border-t border-white/10 pt-6">
                    {isOwner && (
                      <button onClick={() => startEdit("datetime")} className="absolute top-6 right-0 p-2 text-white/40 hover:text-white transition-opacity" data-testid="edit-datetime-btn">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    <div className="flex flex-wrap gap-6 text-white/60">
                      <span className="flex items-center gap-2" data-testid="job-date">
                        <Calendar className="w-5 h-5" />
                        {formatDate(job.date)}{timeRange ? ` at ${timeRange}` : ""}{job.timezone ? ` (${job.timezone.replace("_", " ").split("/").pop()})` : ""}
                      </span>
                      <span className="flex items-center gap-2" data-testid="job-location">
                        <MapPin className="w-5 h-5" />
                        {venueText ? `${venueText}, ` : ""}{locationText}
                      </span>
                      {/* Add to Calendar button - only show for hired candidates */}
                      {token && !isOwner && hireInfo && (hireInfo.status === "confirmed" || hireInfo.status === "hired") && user?.user_id === hireInfo.candidate_id && (
                        <a
                          href={`${API}/oauth/calendar/download-ics/${jobId}`}
                          download
                          className="flex items-center gap-2 text-coral hover:text-coral/80 transition-colors text-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            // Need to add auth header, so use fetch
                            fetch(`${API}/oauth/calendar/download-ics/${jobId}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            })
                            .then(r => r.blob())
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `gottasecond-${job.title?.replace(/\s+/g, '-').toLowerCase() || 'job'}.ics`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            });
                          }}
                          data-testid="download-calendar-btn"
                        >
                          <Calendar className="w-4 h-4" />
                          Add to Calendar
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Pay Section - editable */}
                {editSection === "pay" ? (
                  <div className="space-y-4 mt-6 p-4 border border-white/20 rounded-sm" data-testid="edit-pay-form">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold">Edit Pay</h2>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditSection(null)} className="text-white/60"><X className="w-4 h-4" /></Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-white text-black hover:bg-gray-200" data-testid="save-pay-btn">
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-white/60 block mb-1">Payment Type</label>
                        <select value={editData.is_flexible ? "flexible" : editData.payment_type} onChange={(e) => {
                          if (e.target.value === "flexible") setEditData({...editData, is_flexible: true, payment_type: "flexible"});
                          else setEditData({...editData, is_flexible: false, payment_type: e.target.value});
                        }} className="w-full h-10 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3" data-testid="edit-pay-type">
                          <option value="hourly">Hourly</option>
                          <option value="flat">Flat Rate</option>
                          <option value="flexible">Flexible</option>
                        </select>
                      </div>
                      {!editData.is_flexible && editData.payment_type === "hourly" && (
                        <div>
                          <label className="text-sm text-white/60 block mb-1">Hourly Rate ($)</label>
                          <Input type="number" value={editData.hourly_rate} onChange={(e) => setEditData({...editData, hourly_rate: parseFloat(e.target.value) || ""})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-hourly" />
                        </div>
                      )}
                      {!editData.is_flexible && editData.payment_type === "flat" && (
                        <div>
                          <label className="text-sm text-white/60 block mb-1">Flat Rate ($)</label>
                          <Input type="number" value={editData.flat_rate} onChange={(e) => setEditData({...editData, flat_rate: parseFloat(e.target.value) || ""})} className="bg-white/[0.05] border-white/10 text-white" data-testid="edit-flat" />
                        </div>
                      )}
                      {editData.is_flexible && (
                        <>
                          <div>
                            <label className="text-sm text-white/60 block mb-1">Min ($)</label>
                            <Input type="number" value={editData.rate_min} onChange={(e) => setEditData({...editData, rate_min: parseFloat(e.target.value) || ""})} className="bg-white/[0.05] border-white/10 text-white" />
                          </div>
                          <div>
                            <label className="text-sm text-white/60 block mb-1">Max ($)</label>
                            <Input type="number" value={editData.rate_max} onChange={(e) => setEditData({...editData, rate_max: parseFloat(e.target.value) || ""})} className="bg-white/[0.05] border-white/10 text-white" />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  compensationText && (
                    <div className="relative group mt-6 border-t border-white/10 pt-6">
                      {isOwner && (
                        <button onClick={() => startEdit("pay")} className="absolute top-6 right-0 p-2 text-white/40 hover:text-white transition-opacity" data-testid="edit-pay-btn">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <span className="flex items-center gap-2 text-white/60" data-testid="job-compensation">
                        <DollarSign className="w-5 h-5" />
                        {compensationText}
                      </span>
                    </div>
                  )
                )}
              </Card>

              {/* View Candidates button for owner */}
              {isOwner && !["filled", "completed", "expired", "withdrawn"].includes(job.status) && (
                <Link to={`/jobs/${jobId}/candidates`}>
                  <Card className="bg-white/[0.03] border-white/10 p-6 mb-6 hover:bg-white/[0.06] transition-colors cursor-pointer" data-testid="view-candidates-card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Users className="w-6 h-6 text-[#3498DB]" />
                        <div>
                          <h3 className="text-lg font-semibold">View Matched Candidates</h3>
                          <p className="text-white/50 text-sm">
                            {job.match_count !== undefined && job.match_count !== null
                              ? `${job.match_count} candidate${job.match_count !== 1 ? 's' : ''} match this job`
                              : "See who matches your requirements"}
                          </p>
                        </div>
                      </div>
                      {job.match_count !== undefined && job.match_count !== null && (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-[#3498DB]">{job.match_count}</span>
                        </div>
                      )}
                      <ArrowLeft className="w-5 h-5 text-white/40 rotate-180" />
                    </div>
                  </Card>
                </Link>
              )}

              {/* Hired Candidate Info - shown for pending_confirmation, confirmed, and hired */}
              {isOwner && hireInfo && (
                <Card className={`p-6 mb-6 ${
                  hireInfo.status === "confirmed" || hireInfo.status === "hired"
                    ? "bg-green-500/5 border-green-500/20"
                    : "bg-amber-500/5 border-amber-500/20"
                }`} data-testid="hire-info-card">
                  <div className="flex items-center gap-2 mb-4">
                    {hireInfo.status === "pending_confirmation" ? (
                      <>
                        <Clock className="w-5 h-5 text-amber-400" />
                        <h2 className="text-lg font-semibold text-amber-300">Pending Confirmation</h2>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <h2 className="text-lg font-semibold text-green-300">Confirmed</h2>
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-start gap-4 mb-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={hireInfo.candidate_image} />
                      <AvatarFallback className="bg-white/10 text-white text-lg">{(hireInfo.candidate_name || "?").charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{hireInfo.candidate_name}</p>
                      {hireInfo.status === "pending_confirmation" ? (
                        <>
                          <p className="text-amber-300/70 text-sm">Awaiting candidate's response</p>
                          {hireInfo.confirmation_deadline && (
                            <p className="text-white/40 text-xs mt-1">
                              Deadline: {new Date(hireInfo.confirmation_deadline).toLocaleString()}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-green-300/70 text-sm">
                          Confirmed {hireInfo.confirmed_at ? new Date(hireInfo.confirmed_at).toLocaleDateString() : ""}
                        </p>
                      )}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <Link to={`/messages?to=${hireInfo.candidate_id || ""}`}>
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                            <MessageSquare className="w-4 h-4 mr-1" /> Message
                          </Button>
                        </Link>
                        {hireInfo.status === "pending_confirmation" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={async () => {
                              if (!window.confirm("Withdraw this hire offer? The job will reopen.")) return;
                              const res = await fetch(`${API}/hiring/withdraw/${hireInfo.hire_id}`, {
                                method: "POST",
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (res.ok) {
                                toast.success("Hire offer withdrawn. Job reopened.");
                                fetchJob();
                                fetchHireInfo();
                              } else {
                                toast.error("Failed to withdraw");
                              }
                            }}
                            data-testid="withdraw-hire-btn"
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Withdraw Offer
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Info - only after confirmed */}
                  {(hireInfo.status === "confirmed" || hireInfo.status === "hired") && hireInfo.candidate_email && (
                    <div className="border-t border-white/10 pt-4 mb-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Contact Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {hireInfo.candidate_email && (
                          <a href={`mailto:${hireInfo.candidate_email}`} className="flex items-center gap-3 p-3 bg-white/5 rounded hover:bg-white/10 transition-colors">
                            <Mail className="w-5 h-5 text-[#FF6B6B]" />
                            <span className="text-sm">{hireInfo.candidate_email}</span>
                          </a>
                        )}
                        {hireInfo.candidate_phone && (
                          <a href={`tel:${hireInfo.candidate_phone}`} className="flex items-center gap-3 p-3 bg-white/5 rounded hover:bg-white/10 transition-colors">
                            <Users className="w-5 h-5 text-[#FF6B6B]" />
                            <span className="text-sm">{hireInfo.candidate_phone}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Payment Info - only after confirmed */}
                  {(hireInfo.status === "confirmed" || hireInfo.status === "hired") && hireInfo.payment_info && (
                    <div className="border-t border-white/10 pt-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" /> Payment Information
                      </h3>
                      {hireInfo.payment_info.methods?.length > 0 ? (
                        <div className="space-y-2">
                          {hireInfo.payment_info.methods.map((m, i) => (
                            <div key={i} className="p-3 bg-white/5 rounded">
                              <span className="text-sm font-medium">{m.type}:</span>
                              <span className="text-sm text-white/70 ml-2">{m.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/40 text-sm">No payment methods on file.</p>
                      )}
                    </div>
                  )}

                  {/* Hire Message */}
                  {hireInfo.hire_message && (
                    <div className="border-t border-white/10 pt-4 mt-4">
                      <p className="text-xs text-white/40 mb-1">Message sent with hire:</p>
                      <p className="text-sm text-white/70 italic">"{hireInfo.hire_message}"</p>
                    </div>
                  )}

                  {/* Contract Info */}
                  {contractInfo && (
                    <div className="border-t border-white/10 pt-4 mt-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <ScrollText className="w-4 h-4" /> Contract
                      </h3>
                      <Link to={`/contracts/${contractInfo.contract_id}`}>
                        <div className="flex items-center justify-between p-3 bg-white/5 rounded hover:bg-white/10 transition-colors" data-testid="contract-link">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-[#FF6B6B]" />
                            <div>
                              <p className="text-sm font-medium">{contractInfo.template_title || "Contract"}</p>
                              <p className="text-xs text-white/40">{contractInfo.contract_type === "per_job" ? "Per Job" : "Per Person"}</p>
                            </div>
                          </div>
                          {contractInfo.status === "signed" ? (
                            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded">
                              <CheckCircle className="w-3.5 h-3.5" /> Signed
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded">
                              <PenTool className="w-3.5 h-3.5" /> Awaiting Signature
                            </span>
                          )}
                        </div>
                      </Link>
                    </div>
                  )}
                </Card>
              )}
              {/* Hire pending confirmation — shown to candidate */}
              {!isOwner && hireInfo && hireInfo.status === "pending_confirmation" && user?.user_id === hireInfo.candidate_id && (
                <Card className="bg-amber-500/5 border-amber-500/20 p-6 mb-6" data-testid="hire-confirm-prompt">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-300">Action Required: Accept or Decline This Job</h3>
                      <p className="text-white/50 text-sm mt-1">
                        You've been hired for this job. Please review and respond.
                        {hireInfo.confirmation_deadline && (
                          <span className="block mt-1 text-amber-300/70">
                            Deadline: {new Date(hireInfo.confirmation_deadline).toLocaleString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <Link to={`/hiring/confirm/${hireInfo.hire_id}`}>
                      <Button className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold" data-testid="review-accept-btn">
                        Review & Accept
                      </Button>
                    </Link>
                  </div>
                </Card>
              )}

              {/* Signed contract — shown to candidate */}
              {!isOwner && contractInfo && contractInfo.status === "signed" && contractInfo.candidate_id === user?.user_id && (
                <Link to={`/contracts/${contractInfo.contract_id}`}>
                  <Card className="bg-green-500/5 border-green-500/20 p-4 mb-6 hover:bg-green-500/10 transition-colors" data-testid="contract-signed-card">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-green-300">Contract signed — View {contractInfo.template_title || "contract"}</span>
                    </div>
                  </Card>
                </Link>
              )}

              {/* Job Actions for owner */}
              {isOwner && (
                <Card className="bg-white/[0.03] border-white/10 p-4 mb-6" data-testid="job-actions-card">
                  <div className="flex flex-wrap items-center gap-3">
                    {job.status === "cancelled" && (
                      <Button 
                        onClick={handleReopenJob} 
                        variant="outline" 
                        className="border-white/20 text-white/70 hover:bg-white/10"
                        data-testid="reopen-job-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" /> Reopen Job
                      </Button>
                    )}
                    {job.status === "open" && (
                      <>
                        <Button onClick={handleMarkHired} variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="mark-hired-btn">
                          <UserCheck className="w-4 h-4 mr-2" /> Mark as Hired
                        </Button>
                        <Button onClick={handleMarkFilled} variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="mark-filled-btn">
                          Mark as Filled
                        </Button>
                        <Button onClick={handleWithdrawJob} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="withdraw-job-btn">
                          <X className="w-4 h-4 mr-2" /> Withdraw
                        </Button>
                        <Button onClick={handleCancelJob} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="cancel-job-btn">
                          <X className="w-4 h-4 mr-2" /> Cancel Job
                        </Button>
                      </>
                    )}
                    {job.status === "pending_confirmation" && (
                      <>
                        <Button onClick={handleMarkHired} variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10" data-testid="mark-hired-btn">
                          <UserCheck className="w-4 h-4 mr-2" /> Mark as Hired
                        </Button>
                        <Button onClick={handleWithdrawJob} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="withdraw-job-btn">
                          <X className="w-4 h-4 mr-2" /> Withdraw
                        </Button>
                      </>
                    )}
                    {job.status === "filled" && (
                      <>
                        <Button onClick={handleMarkCompleted} variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="mark-completed-btn">
                          <CheckCircle className="w-4 h-4 mr-2" /> Mark Completed
                        </Button>
                        <Button onClick={handleReopenJob} variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="reopen-job-btn">
                          <RotateCcw className="w-4 h-4 mr-2" /> Reopen Job
                        </Button>
                      </>
                    )}
                    {job.status === "expired" && (
                      <>
                        <Button onClick={handleReopenJob} variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="reopen-job-btn">
                          <RotateCcw className="w-4 h-4 mr-2" /> Reopen Job
                        </Button>
                        <Button onClick={handleMarkHired} variant="outline" className="border-white/20 text-white/70 hover:bg-white/10" data-testid="mark-hired-btn">
                          <UserCheck className="w-4 h-4 mr-2" /> Mark as Hired
                        </Button>
                        <Button onClick={handleWithdrawJob} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="withdraw-job-btn">
                          <X className="w-4 h-4 mr-2" /> Withdraw
                        </Button>
                      </>
                    )}
                    {job.status === "completed" && (
                      <p className="text-white/40 text-sm">This job is completed. No further actions available.</p>
                    )}
                    {job.status === "withdrawn" && (
                      <p className="text-white/40 text-sm">This job has been withdrawn and archived.</p>
                    )}
                  </div>
                </Card>
              )}

              {/* Applicants section for owner */}
              {isOwner && (
                <Card className="bg-white/[0.03] border-white/10 p-8" data-testid="applicants-card">
                  <h2 className="text-xl font-semibold mb-6">Applicants ({apps.length})</h2>
                  {apps.length === 0 ? (
                    <p className="text-white/50">No applications yet</p>
                  ) : (
                    <div className="space-y-4">
                      {[...apps].sort((a, b) => (a.dismissed ? 1 : 0) - (b.dismissed ? 1 : 0)).map((app) => {
                        const isDismissed = app.dismissed;
                        const applicantId = app.user_id || app.applicant_id;
                        const isHired = hireInfo && hireInfo.candidate_id === applicantId;
                        const hirePending = isHired && hireInfo.status === "pending_confirmation";
                        const hireConfirmed = isHired && (hireInfo.status === "confirmed" || hireInfo.status === "hired");
                        return (
                          <div key={app.application_id || applicantId} className={`border rounded-sm p-4 transition-all ${isDismissed ? "border-white/5 opacity-40" : isHired ? "border-[#FF6B6B]/40 bg-[#FF6B6B]/5" : "border-white/10"}`} data-testid={`applicant-${app.application_id}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10">
                                  <AvatarImage src={app.user_profile_image || app.applicant?.profile_image} />
                                  <AvatarFallback className="bg-white/[0.05] text-white">{(app.user_name || app.applicant?.name || "?").charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <Link to={`/profile/${applicantId}`} className="font-semibold hover:underline">{app.user_name || app.applicant?.name || "Unknown"}</Link>
                                  <div className="flex items-center gap-2 text-sm text-white/50">
                                    <Clock className="w-3 h-3" />Applied {new Date(app.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              {isDismissed && <Badge className="bg-white/5 text-white/30 border-white/10 rounded-sm">Dismissed</Badge>}
                              {hirePending && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 rounded-sm animate-pulse" data-testid={`hire-pending-badge-${app.application_id}`}>Offer Sent — Awaiting Response</Badge>}
                              {hireConfirmed && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 rounded-sm" data-testid={`hire-confirmed-badge-${app.application_id}`}>Hired</Badge>}
                            </div>
                            {!isDismissed && app.message && <p className="text-white/60 mt-3">{app.message}</p>}
                            {!isDismissed && app.proposed_rate && <p className="text-white/60 mt-2">Proposed rate: ${app.proposed_rate}/hr</p>}
                            <div className="flex gap-2 mt-4">
                              {isDismissed ? (
                                <Button size="sm" variant="outline" onClick={() => handleRestoreApplicant(app.application_id)} className="border-white/10 text-white/40 hover:bg-white/5 rounded-sm" data-testid={`restore-${app.application_id}`}><Undo2 className="w-4 h-4 mr-1" />Restore</Button>
                              ) : isHired ? (
                                <Button size="sm" variant="outline" onClick={() => navigate(`/messages?to=${applicantId}&job=${jobId}`)} className="border-white/15 text-white/70 hover:bg-white/5 rounded-sm" data-testid={`message-applicant-${app.application_id}`}><MessageSquare className="w-4 h-4 mr-1" />Message</Button>
                              ) : (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => navigate(`/messages?to=${applicantId}&job=${jobId}`)} className="border-white/15 text-white/70 hover:bg-white/5 rounded-sm" data-testid={`message-applicant-${app.application_id}`}><MessageSquare className="w-4 h-4 mr-1" />Message</Button>
                                  <Button size="sm" onClick={() => handleHireApplicant(app)} className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-sm font-bold" data-testid={`hire-applicant-${app.application_id}`}><UserCheck className="w-4 h-4 mr-1" />Hire</Button>
                                  <Button size="sm" variant="outline" onClick={() => handleDismissApplicant(app.application_id)} className="border-white/10 text-white/40 hover:bg-white/5 rounded-sm" data-testid={`dismiss-${app.application_id}`}><EyeOff className="w-4 h-4 mr-1" />Dismiss</Button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              )}

              {/* Review Section - shows after event date */}
              {token && job.date && (() => { const p = job.date.split("T")[0].split("-"); return new Date(+p[0], +p[1]-1, +p[2]) < new Date(); })() && (isOwner || (hireInfo && (hireInfo.status === "confirmed" || hireInfo.status === "hired"))) && (
                <Card className="bg-white/[0.03] border-white/10 p-6 mt-6" data-testid="review-section">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-coral" />
                    {existingReview ? "Your Review" : "Leave a Review"}
                  </h3>
                  {existingReview ? (
                    <div>
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={"w-5 h-5 " + (s <= existingReview.rating ? "fill-amber-400 text-amber-400" : "text-white/20")} />
                        ))}
                      </div>
                      {existingReview.comment && <p className="text-white/60 text-sm">{existingReview.comment}</p>}
                    </div>
                  ) : showReviewForm ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-white/60 mb-2">Rating</p>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(s => (
                            <button key={s} type="button" onClick={() => setReviewRating(s)} className="p-0.5" data-testid={"star-" + s}>
                              <Star className={"w-7 h-7 transition-colors cursor-pointer " + (s <= reviewRating ? "fill-amber-400 text-amber-400" : "text-white/20 hover:text-amber-400/50")} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-white/60 mb-2">Comment (optional)</p>
                        <Textarea
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          placeholder="How was the experience?"
                          className="bg-white/5 border-white/10 text-white min-h-[80px]"
                          data-testid="review-comment"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={submitReview} disabled={submittingReview || reviewRating < 1} className="bg-coral hover:bg-coral/90 text-white" data-testid="submit-review-btn">
                          {submittingReview ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Star className="w-4 h-4 mr-1" />}
                          Submit Review
                        </Button>
                        <Button variant="ghost" onClick={() => setShowReviewForm(false)} className="text-white/40 hover:text-white">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={() => setShowReviewForm(true)} className="bg-white/10 hover:bg-white/20 text-white" data-testid="open-review-btn">
                      <Star className="w-4 h-4 mr-2" /> Write a Review
                    </Button>
                  )}
                </Card>
              )}
            </div>
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="studio-card">
                <h3 className="text-lg font-semibold mb-4">Posted by</h3>
                <Link to={`/profile/${job.studio_id || creatorId}?view=studio`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={job.creator?.profile_image} />
                    <AvatarFallback className="bg-white/[0.05] text-white text-lg">{(job.studio_name || job.creator?.name || "?").charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{job.studio_name || job.creator?.name || "Unknown"}</p>
                    <p className="text-white/50 text-sm">View Profile</p>
                  </div>
                </Link>
              </Card>

              {/* Match score for assistants viewing this job */}
              {job.my_match && !isOwner && (
                <Card className={`border p-6 ${
                  job.my_match.tier === "excellent" ? "bg-green-500/10 border-green-500/30" :
                  job.my_match.tier === "good" ? "bg-yellow-500/10 border-yellow-500/30" :
                  "bg-white/[0.03] border-white/10"
                }`} data-testid="my-match-score">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Your Match</h3>
                    <span className={`text-2xl font-bold ${
                      job.my_match.tier === "excellent" ? "text-green-400" :
                      job.my_match.tier === "good" ? "text-yellow-400" : "text-white/60"
                    }`}>{job.my_match.score}%</span>
                  </div>
                  <p className="text-sm text-white/50 capitalize">{job.my_match.tier} match</p>
                  {job.my_match.factors && (
                    <div className="mt-3 space-y-1">
                      {Object.entries(job.my_match.factors).map(([key, val]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span className="text-white/40 capitalize">{key.replace(/_/g, " ")}</span>
                          <span className={val === "No match" ? "text-red-400/60" : "text-white/60"}>{val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              <Card className="bg-white/[0.03] border-white/10 p-6" data-testid="details-sidebar">
                <h3 className="text-lg font-semibold mb-4">Job Details</h3>
                <dl className="space-y-4">
                  {(job.genre || job.job_type) && (
                    <div>
                      <dt className="text-white/50 text-sm">Job Type</dt>
                      <dd className="font-medium" data-testid="job-genre-display">{GENRES.find((g) => g.value === (job.genre || job.job_type))?.label || job.genre || job.job_type}</dd>
                    </div>
                  )}
                  {job.role_needed && (
                    <div>
                      <dt className="text-white/50 text-sm">Role</dt>
                      <dd className="font-medium capitalize">
                        {job.role_needed.replace(/_/g, " ")}
                        {job.requires_lead && <span className="ml-2 text-xs text-amber-400/80 bg-amber-400/10 px-1.5 py-0.5 rounded">Lead Required</span>}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-white/50 text-sm">Date</dt>
                    <dd className="font-medium">{formatDate(job.date)}</dd>
                  </div>
                  {timeRange && (
                    <div>
                      <dt className="text-white/50 text-sm">Time</dt>
                      <dd className="font-medium">{timeRange}</dd>
                    </div>
                  )}
                  {locationText !== "Location TBD" && (
                    <div>
                      <dt className="text-white/50 text-sm">Location</dt>
                      <dd className="font-medium">{venueText ? `${venueText}, ` : ""}{locationText}</dd>
                    </div>
                  )}
                  {compensationText && (
                    <div>
                      <dt className="text-white/50 text-sm">Compensation</dt>
                      <dd className="font-medium">{compensationText}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-white/50 text-sm">Applicants</dt>
                    <dd className="font-medium">{apps.length}</dd>
                  </div>
                </dl>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Hire Applicant Dialog */}
      <Dialog open={hireDialogOpen} onOpenChange={setHireDialogOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Hire {hireCandidate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-white/70 text-sm">This will mark the job as filled and notify the candidate.</p>

            {contractTemplates.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2"><FileText className="w-4 h-4 inline mr-1" />Attach Contract</label>
                {existingContract && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-white/10 border border-white/20 rounded text-xs text-white/70">
                    <FileText className="w-3.5 h-3.5" />Existing contract on file (signed {new Date(existingContract.signed_at).toLocaleDateString()})
                  </div>
                )}
                <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm" data-testid="hire-contract-template-select">
                  <option value="">No contract</option>
                  {contractTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>{t.title} ({t.contract_type === "per_job" ? "Per Job" : "Per Person"})</option>
                  ))}
                </select>
                <p className="text-xs text-white/30 mt-1">The candidate will be asked to sign before the job begins.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2"><DollarSign className="w-4 h-4 inline mr-1" />Final Agreed Rate</label>
              <div className="flex gap-2">
                <select value={rateType} onChange={(e) => setRateType(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm" data-testid="hire-rate-type-select">
                  <option value="hourly">Per Hour</option>
                  <option value="flat">Per Job (Flat)</option>
                </select>
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <Input value={finalRate} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setFinalRate(v); }} placeholder={job?.hourly_rate ? String(job.hourly_rate) : job?.flat_rate ? String(job.flat_rate) : "0"} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-7" data-testid="hire-final-rate-input" inputMode="decimal" />
                </div>
              </div>
              <p className="text-xs text-white/30 mt-1">Enter the numeric rate. This will be used in the contract and offer.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Message to candidate (optional)</label>
              <Textarea value={hireMessage} onChange={(e) => setHireMessage(e.target.value)} placeholder="Welcome to the team! Looking forward to working with you..." className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[100px]" maxLength={500} />
              <div className="text-xs text-white/40 mt-1">{hireMessage.length}/500 characters</div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setHireDialogOpen(false)} className="flex-1 border-white/20 text-white hover:bg-white/10" disabled={hiring}>Cancel</Button>
              <Button onClick={confirmHireApplicant} disabled={hiring} className="flex-1 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold" data-testid="confirm-hire-applicant-btn">
                {hiring ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Hiring...</>) : (<><UserCheck className="w-4 h-4 mr-2" />Confirm Hire</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

export default JobDetail;
